import { useMemo } from 'react';
import {
  useJournalFlow as useJournalFlowCore,
  type JournalFlowHookReturn,
  type JournalFlowWarmupPayload,
  type JournalFlowWritingPayload,
  type JournalStep,
} from '@emovia/journal-flow';
import type { JournalEntry } from '@domain/journal';
import { logError, logInfo, logWarn } from '@/lib/logger';
import { AUTOSAVE_DELAY, MAX_CARDS, requestJson } from '@/pages/patients/journals/flowUtils';

function ensureOk<T>(result: { ok: boolean; data?: T }, fallbackMessage: string): T {
  if (result.ok && result.data) {
    return result.data;
  }
  throw new Error(fallbackMessage);
}

export function useJournalFlow(entry: JournalEntry): JournalFlowHookReturn {
  const api = useMemo(
    () => ({
      advanceStep: async (entryId: number, step?: JournalStep) => {
        const body = step ? JSON.stringify({ step }) : undefined;
        const result = await requestJson<JournalEntry>(`/patients/journals/${entryId}/advance`, {
          method: 'POST',
          body,
        });

        const payload = ensureOk(result, 'advance_failed');
        return payload;
      },
      updateWarmup: async (entryId: number, payload: JournalFlowWarmupPayload) => {
        const result = await requestJson<JournalEntry>(`/patients/journals/${entryId}/warmup`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        });

        const data = ensureOk<JournalEntry>(result, 'warmup_failed');
        return data;
      },
      toggleCard: async (entryId: number, cardId: number) => {
        const result = await requestJson<JournalEntry>(
          `/patients/journals/${entryId}/cards/${cardId}/toggle`,
          {
            method: 'POST',
          },
        );

        const data = ensureOk(result, 'card_response_failed');
        return data;
      },
      shuffleHand: async (entryId: number) => {
        const result = await requestJson<JournalEntry>(`/patients/journals/${entryId}/shuffle`, {
          method: 'POST',
        });

        return ensureOk(result, 'shuffle_failed');
      },
      saveWriting: async (entryId: number, payload: JournalFlowWritingPayload) => {
        const result = await requestJson<JournalEntry>(`/patients/journals/${entryId}/writing`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        });

        const data = ensureOk(result, 'writing_failed');
        return data;
      },
    }),
    [],
  );

  const logger = useMemo(
    () => ({
      info: (event: string, payload?: Record<string, unknown>) => logInfo(event, payload),
      warn: (event: string, payload?: Record<string, unknown>) => logWarn(event, payload),
      error: (event: string, error: unknown, payload?: Record<string, unknown>) => {
        if (payload) {
          logError(event, error, payload);
        } else {
          logError(event, error);
        }
      },
    }),
    [],
  );

  return useJournalFlowCore({
    entry,
    api,
    logger,
    autosaveDelayMs: AUTOSAVE_DELAY,
    maxSelectableCards: MAX_CARDS,
    onInvalidStep: (value, entry) => {
      logError('journal_flow.invalid_step_web', new Error('Invalid journal step'), {
        entry,
        currentStep: value ?? null,
      });
    },
  });
}
